/**
 * Catálogo das ETAPAS que existem na planilha do gerenciador de produção
 * (aba GERENCIAL, "GERENCIADOR DE PRODUÇÃO <ano>.xlsm").
 *
 * É a lista que o gestor vê ao criar um setor no mapa: todo setor novo é
 * amarrado a UMA etapa desta lista, e é ela que decide quais ambientes a
 * planilha empurra para o setor. Vários setores podem apontar para a mesma
 * etapa (quem recebe os ambientes é decidido no cadastro do setor, não aqui).
 *
 * Como a planilha marca o andamento (regra do dono): DATA PREENCHIDA = ETAPA
 * FINALIZADA. O ambiente está na etapa SEGUINTE à mais avançada com data —
 * etapas anteriores sem data contam como concluídas (o PPCP às vezes só
 * digita o último marco). É a mesma regra de `setores.js`, aqui em grão fino:
 * cada coluna de data virou uma etapa, em vez dos cinco setores fixos.
 *
 * Duas convenções da planilha que mudam a leitura:
 * - "." na coluna = NÃO SE APLICA (usinagem é "." em 100% dos ambientes
 *   ativos hoje). A etapa é pulada: não segura o ambiente nem conta como
 *   marco com data.
 * - "X" = não se aplica nas colunas de item (terceiros, acabamento), e as
 *   colunas BI–BN / BQ–BT / CA–CD são FÓRMULA ESPELHO das quantidades
 *   (`=AL5`, `=AC5`…): quando a célula de origem está vazia o espelho devolve
 *   ZERO. Zero é "não se aplica" — quem tratar zero como item esperado
 *   inventa pendência em todo ambiente que ainda não foi para a fábrica.
 *
 * ETAPA SEQUENCIAL faz o ambiente andar (é candidata a virar sala no mapa).
 * ETAPA PARALELA (terceiros, acabamento, insumos) acontece ao lado: não tira
 * o ambiente da sequência, só pendura pendência nele.
 *
 * Nada aqui grava: é leitura pura da linha da planilha.
 */
import { hasValue } from "../../../utils/data";
import { F } from "../../../utils/gerencialFields";
import { naOrdemDaLinha } from "../../../utils/fluxoExecutivo";
import { toDate } from "../../../utils/status";
import { ambientePrecisaItem } from "../../../utils/terceiros";

export const TIPO_ETAPA = Object.freeze({ SEQUENCIAL: "sequencial", PARALELA: "paralela" });

// Marcadores de "não se aplica" nas colunas de etapa.
const NAO_SE_APLICA = Object.freeze(["."]);
const MARCADOR_X = "X";

/** Macro-grupos da linha 1 do cabeçalho da aba GERENCIAL, na ordem das colunas. */
export const MACRO_GRUPOS = Object.freeze([
  { codigo: "info_pedido", rotulo: "Info pedido", rotuloPlanilha: "INFO PEDIDO", colunas: "A:X" },
  { codigo: "compras_sistema", rotulo: "Compras sistema", rotuloPlanilha: "COMPRAS SISTEMA", colunas: "AX:BB" },
  { codigo: "manufatura_maquinas", rotulo: "Manufatura máquinas", rotuloPlanilha: "MANUFATURA MÁQUINAS", colunas: "BC:BH" },
  {
    codigo: "terceiros_componentes",
    rotulo: "Fornecedores terceiros e componentes",
    rotuloPlanilha: "FORNECEDORES TERCEIROS / COMPONENTES",
    colunas: "BI:BP",
  },
  { codigo: "manufatura_marcenaria", rotulo: "Manufatura marcenaria", rotuloPlanilha: "MANUFATURA MARCENARIA", colunas: "BQ:BZ" },
  { codigo: "acabamento", rotulo: "Acabamento", rotuloPlanilha: "ACABAMENTO", colunas: "CA:CG" },
  { codigo: "expedicao_entrega", rotulo: "Expedição e entrega", rotuloPlanilha: "EXPEDIÇÃO E ENTREGA", colunas: "CH:CR" },
]);

// Grupos de etapa paralela que a planilha fecha numa única coluna: o item
// individual deixa de ser pendência quando a coluna de fechamento tem data.
const GRUPO_TERCEIROS = "terceiros";
const GRUPO_ACABAMENTO = "acabamento";

// Ordem do fluxo. `ordem` é preenchida abaixo pela posição no array.
const FLUXO = [
  // ---------------------------------------------------------- escritório
  {
    codigo: "liberacao_obra",
    rotulo: "Liberação da obra",
    rotuloPlanilha: "LIBERAÇÃO DA OBRA",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "info_pedido",
    coluna: "G",
    campo: F.LIBERACAO_DA_OBRA,
    observacao:
      "OBRA FUTURA (F) é fórmula: vale NÃO quando esta coluna tem data. Ambiente ativo sempre passou daqui; " +
      "sem data ele é obra futura e fica na bandeja, não na etapa.",
  },
  {
    codigo: "definicao_acabamentos",
    rotulo: "Definição de acabamentos",
    rotuloPlanilha: "DEFINIÇÃO ACABAMENTOS REALIZADO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "info_pedido",
    coluna: "H",
    campo: F.DEFINICAO_ACABAMENTOS_REALIZADO,
    colunaLimite: "I",
    campoLimite: F.DEFINICAO_ACABAMENTOS_LIMITE,
  },
  {
    codigo: "medicao",
    rotulo: "Medição",
    rotuloPlanilha: "MEDIÇÃO REALIZADO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "info_pedido",
    coluna: "J",
    campo: F.MEDICAO_REALIZADO,
    colunaLimite: "K",
    campoLimite: F.MEDICAO_LIMITE,
  },
  {
    codigo: "projeto_executivo",
    rotulo: "Projeto executivo",
    rotuloPlanilha: "EXECUTIVO REALIZADO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "info_pedido",
    coluna: "L",
    campo: F.EXECUTIVO_REALIZADO,
    colunaLimite: "M",
    campoLimite: F.EXECUTIVO_LIMITE,
  },
  // Revisão antes da aprovação (ordem nova, 28/10/2026). Linha cuja ordem
  // é a antiga troca as duas de lugar em `etapasSequenciaisDa`.
  {
    codigo: "revisao",
    rotulo: "Revisão do projeto",
    rotuloPlanilha: "REVISÃO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "info_pedido",
    coluna: "R",
    campo: F.REVISAO,
  },
  {
    codigo: "aprovacao_executivo",
    rotulo: "Aprovação do executivo",
    rotuloPlanilha: "APROVAÇÃO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "info_pedido",
    coluna: "P",
    campo: F.APROVACAO_DO_EXECUTIVO,
    colunaLimite: "O",
    campoLimite: F.APROVACAO_LIMITE,
  },
  {
    codigo: "otimizacao_lote",
    rotulo: "Otimização do lote",
    rotuloPlanilha: "OTMIZAÇÃO LOTE",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "info_pedido",
    coluna: "S",
    campo: F.OTIMIZACAO_DO_LOTE,
    observacao:
      "Marco que abre a fábrica: todos os limites de COMPRAS a ENTREGA são fórmulas a partir desta data. " +
      "Sem ela o ambiente não tem prazo de fábrica — vale a ENTREGA PREVISTA (N).",
  },
  // ------------------------------------------------------------- compras
  {
    codigo: "compras_mdf_bordo",
    rotulo: "Compras — MDF e bordo",
    rotuloPlanilha: "MDF / BORDO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "compras_sistema",
    coluna: "AY",
    campo: F.MDF_BORDO,
    colunaLimite: "BA",
    campoLimite: F.FORNECIMENTO,
    observacao: "Precede o corte sem exceção: nenhum ambiente do arquivo tem data de CORTE sem data aqui.",
  },
  {
    codigo: "compras_insumos_ferragens",
    rotulo: "Compras — insumos e ferragens",
    rotuloPlanilha: "INSUMOS / FERRAGENS",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "compras_sistema",
    coluna: "AZ",
    campo: F.INSUMOS_FERRAGENS,
    colunaLimite: "BA",
    campoLimite: F.FORNECIMENTO,
    aplicavelApos: F.OTIMIZACAO_DO_LOTE,
    observacao: "Não gateia o corte (há ambiente com insumos antes do MDF e vice-versa); trava a marcenaria.",
  },
  // ------------------------------------------------------------ máquinas
  {
    codigo: "corte",
    rotulo: "Corte",
    rotuloPlanilha: "CORTE",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "manufatura_maquinas",
    coluna: "BC",
    campo: F.DATA_DE_CORTE,
    colunaLimite: "BG",
    campoLimite: F.FINAL_MAQUINAS,
  },
  {
    codigo: "bordo",
    rotulo: "Bordo",
    rotuloPlanilha: "BORDO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "manufatura_maquinas",
    coluna: "BD",
    campo: F.DATA_DE_BORDO,
    colunaLimite: "BG",
    campoLimite: F.FINAL_MAQUINAS,
  },
  {
    codigo: "furacao",
    rotulo: "Furação",
    rotuloPlanilha: "FURAÇÃO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "manufatura_maquinas",
    coluna: "BE",
    campo: F.DATA_DE_FURACAO,
    colunaLimite: "BG",
    campoLimite: F.FINAL_MAQUINAS,
  },
  {
    codigo: "usinagem",
    rotulo: "Usinagem",
    rotuloPlanilha: "USINAGEM",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "manufatura_maquinas",
    coluna: "BF",
    campo: F.DATA_DE_USINAGEM,
    colunaLimite: "BG",
    campoLimite: F.FINAL_MAQUINAS,
    observacao:
      'Quase sempre "." (não se aplica): 498 das 509 linhas do arquivo. Setor amarrado aqui fica vazio na ' +
      "maior parte do tempo — o ambiente pula direto para a marcenaria.",
  },
  // ------------------------------------------------- terceiros (paralelo)
  {
    codigo: "terceiros_serralheria",
    rotulo: "Terceiros — serralheria",
    rotuloPlanilha: "SERRALHERIA",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "terceiros_componentes",
    coluna: "BI",
    campo: F.SERRALHERIA_TERCEIROS,
    colunaLimite: "BP",
    campoLimite: F.ENTREGA_TERCEIROS,
    grupo: GRUPO_TERCEIROS,
  },
  {
    codigo: "terceiros_perfil_aluminio",
    rotulo: "Terceiros — perfil de alumínio",
    rotuloPlanilha: "PERFIL ALUMINIO PINTURA",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "terceiros_componentes",
    coluna: "BJ",
    campo: F.PERFIL_ALUMINIO_TERCEIROS,
    colunaLimite: "BP",
    campoLimite: F.ENTREGA_TERCEIROS,
    grupo: GRUPO_TERCEIROS,
  },
  {
    codigo: "terceiros_portas_frentes",
    rotulo: "Terceiros — portas e frentes de alumínio",
    rotuloPlanilha: "PORTAS E FRENTES ALUMÍNIO VIDRO",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "terceiros_componentes",
    coluna: "BK",
    campo: F.PORTAS_FRENTES_TERCEIROS,
    colunaLimite: "BP",
    campoLimite: F.ENTREGA_TERCEIROS,
    grupo: GRUPO_TERCEIROS,
  },
  {
    codigo: "terceiros_vidracaria",
    rotulo: "Terceiros — vidraçaria",
    rotuloPlanilha: "VIDRAÇARIA",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "terceiros_componentes",
    coluna: "BL",
    campo: F.VIDRACARIA,
    colunaLimite: "BP",
    campoLimite: F.ENTREGA_TERCEIROS,
    grupo: GRUPO_TERCEIROS,
  },
  {
    codigo: "terceiros_tapecaria",
    rotulo: "Terceiros — tapeçaria",
    rotuloPlanilha: "TAPEÇARIA",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "terceiros_componentes",
    coluna: "BM",
    campo: F.TAPECARIA_TERCEIROS,
    colunaLimite: "BP",
    campoLimite: F.ENTREGA_TERCEIROS,
    grupo: GRUPO_TERCEIROS,
  },
  {
    codigo: "terceiros_ferragens_especiais",
    rotulo: "Terceiros — ferragens especiais",
    rotuloPlanilha: "FERRAGENS ESPECIAIS",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "terceiros_componentes",
    coluna: "BN",
    campo: F.FERRAGENS_ESPECIAIS_TERCEIROS,
    colunaLimite: "BP",
    campoLimite: F.ENTREGA_TERCEIROS,
    grupo: GRUPO_TERCEIROS,
  },
  {
    codigo: "recebimento_terceiros",
    rotulo: "Recebimento de terceiros",
    rotuloPlanilha: "RECEBIMENTO TERCEIROS",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "terceiros_componentes",
    coluna: "BO",
    campo: F.RECEBIMENTO_TERCEIROS,
    colunaLimite: "BP",
    campoLimite: F.ENTREGA_TERCEIROS,
    fechaGrupo: GRUPO_TERCEIROS,
    observacao: "Fecha todos os itens de terceiros de uma vez. Só 71 das 509 linhas têm data — é coluna pouco usada.",
  },
  // ---------------------------------------------------------- marcenaria
  {
    codigo: "marcenaria",
    rotulo: "Marcenaria",
    rotuloPlanilha: "MARCENARIA TÉRMINO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "manufatura_marcenaria",
    coluna: "BW",
    campo: F.MARCENARIA_TERMINO,
    colunaInicio: "BV",
    campoInicio: F.MARCENARIA_INICIO,
    colunaLimite: "BX",
    campoLimite: F.FINAL_MARCENARIA,
    colunaLimiteReprogramado: "BZ",
    campoLimiteReprogramado: F.FINAL_MARCENARIA_REPROGRAMADO,
    observacao:
      "CAIXARIA/TAMBURATO/ACABAMENTO MANUAL/RIPADO (BQ–BT) são fórmula espelho das quantidades, não datas: " +
      "o que marca a marcenaria é INÍCIO (BV, em execução) e TÉRMINO (BW, concluída).",
  },
  // ----------------------------------------------- acabamento (paralelo)
  {
    codigo: "acabamento_pintura",
    rotulo: "Acabamento — pintura",
    rotuloPlanilha: "PINTURA",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "acabamento",
    coluna: "CA",
    campo: F.PINTURA,
    colunaLimite: "CF",
    campoLimite: F.FINAL_ACABAMENTO,
    colunaLimiteReprogramado: "CG",
    campoLimiteReprogramado: F.FINAL_ACABAMENTO_REPROGRAMADO,
    grupo: GRUPO_ACABAMENTO,
  },
  {
    codigo: "acabamento_verniz",
    rotulo: "Acabamento — verniz",
    rotuloPlanilha: "VERNIZ",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "acabamento",
    coluna: "CB",
    campo: F.VERNIZ_ACAB,
    colunaLimite: "CF",
    campoLimite: F.FINAL_ACABAMENTO,
    colunaLimiteReprogramado: "CG",
    campoLimiteReprogramado: F.FINAL_ACABAMENTO_REPROGRAMADO,
    grupo: GRUPO_ACABAMENTO,
  },
  {
    codigo: "acabamento_pintura_perfil",
    rotulo: "Acabamento — pintura de perfil",
    rotuloPlanilha: "PINTURA PERFIL",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "acabamento",
    coluna: "CC",
    campo: F.PINTURA_PERFIL,
    colunaLimite: "CF",
    campoLimite: F.FINAL_ACABAMENTO,
    colunaLimiteReprogramado: "CG",
    campoLimiteReprogramado: F.FINAL_ACABAMENTO_REPROGRAMADO,
    grupo: GRUPO_ACABAMENTO,
  },
  {
    codigo: "acabamento_pintura_serralheria",
    rotulo: "Acabamento — pintura de serralheria",
    rotuloPlanilha: "PINTURA SERRALHERIA",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "acabamento",
    coluna: "CD",
    campo: F.PINTURA_SERRALHERIA,
    colunaLimite: "CF",
    campoLimite: F.FINAL_ACABAMENTO,
    colunaLimiteReprogramado: "CG",
    campoLimiteReprogramado: F.FINAL_ACABAMENTO_REPROGRAMADO,
    grupo: GRUPO_ACABAMENTO,
  },
  {
    codigo: "acabamento_realizado",
    rotulo: "Acabamento realizado",
    rotuloPlanilha: "ACABAMENTO REALIZADO",
    tipo: TIPO_ETAPA.PARALELA,
    macro: "acabamento",
    coluna: "CE",
    campo: F.ACABAMENTO_REALIZADO,
    colunaLimite: "CF",
    campoLimite: F.FINAL_ACABAMENTO,
    colunaLimiteReprogramado: "CG",
    campoLimiteReprogramado: F.FINAL_ACABAMENTO_REPROGRAMADO,
    fechaGrupo: GRUPO_ACABAMENTO,
    observacao: "Fecha os itens de acabamento de uma vez. Só 40 das 509 linhas têm data.",
  },
  // ------------------------------------------------ expedição e entrega
  {
    codigo: "limpeza_embalagem",
    rotulo: "Limpeza e embalagem",
    rotuloPlanilha: "LIMPEZA / EMBALAGEM",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "expedicao_entrega",
    coluna: "CH",
    campo: F.LIMPEZA_EMBALAGEM,
    colunaLimite: "CJ",
    campoLimite: F.EXPEDICAO,
    colunaLimiteReprogramado: "CM",
    campoLimiteReprogramado: F.EXPEDICAO_REPROGRAMADA,
  },
  {
    codigo: "separacao",
    rotulo: "Separação",
    rotuloPlanilha: "SEPARAÇÃO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "expedicao_entrega",
    coluna: "CI",
    campo: F.SEPARACAO,
    colunaLimite: "CJ",
    campoLimite: F.EXPEDICAO,
    colunaLimiteReprogramado: "CM",
    campoLimiteReprogramado: F.EXPEDICAO_REPROGRAMADA,
  },
  {
    codigo: "carregamento",
    rotulo: "Carregamento",
    rotuloPlanilha: "CARREGAMENTO",
    tipo: TIPO_ETAPA.SEQUENCIAL,
    macro: "expedicao_entrega",
    coluna: "CN",
    campo: F.CARREGAMENTO,
    colunaLimite: "CO",
    campoLimite: F.ENTREGA_CLIENTE,
    colunaLimiteReprogramado: "CR",
    campoLimiteReprogramado: F.DATA_REPROGRAMADA,
    observacao: "Última etapa: com data aqui o ambiente saiu da fábrica e deixa o mapa.",
  },
];

export const ETAPAS_PLANILHA = Object.freeze(
  FLUXO.map((etapa, indice) => Object.freeze({ ordem: indice + 1, ...etapa }))
);

export const ETAPA_POR_CODIGO = Object.freeze(
  Object.fromEntries(ETAPAS_PLANILHA.map((etapa) => [etapa.codigo, etapa]))
);

export const ETAPAS_SEQUENCIAIS = Object.freeze(
  ETAPAS_PLANILHA.filter((etapa) => etapa.tipo === TIPO_ETAPA.SEQUENCIAL)
);

const ORDEM_DO_EXECUTIVO = Object.freeze({
  ehRevisao: (etapa) => etapa.codigo === "revisao",
  ehAprovacao: (etapa) => etapa.codigo === "aprovacao_executivo",
});

/** Etapas sequenciais na ordem do executivo que vale para a linha. */
export function etapasSequenciaisDa(linha) {
  return naOrdemDaLinha(linha, ETAPAS_SEQUENCIAIS, ORDEM_DO_EXECUTIVO);
}

export const ETAPAS_PARALELAS = Object.freeze(
  ETAPAS_PLANILHA.filter((etapa) => etapa.tipo === TIPO_ETAPA.PARALELA)
);

/**
 * Etapa equivalente a cada um dos cinco setores fixos da v2, para a migração
 * não perder as salas e alocações já apontadas. "expedicao" virou a primeira
 * etapa do macro-grupo Expedição e entrega (limpeza e embalagem).
 */
export const MACRO_POR_CODIGO = Object.freeze(
  Object.fromEntries(MACRO_GRUPOS.map((macro) => [macro.codigo, macro]))
);

/**
 * VÍNCULO de um setor com a planilha: uma ETAPA ("Corte") ou um MACRO-GRUPO
 * inteiro ("todo o grupo Expedição e entrega"). Foi decisão do dono que o
 * setor possa apontar para o macro ou para o micro — uma sala de expedição
 * recebe limpeza, separação e carregamento sem o gestor precisar de três
 * salas. Os dois vivem no mesmo campo, e nenhum código se repete entre eles
 * (o backend guarda a mesma regra em `mapa_producao/etapas.py`).
 */
export function vinculoExiste(codigo) {
  return Boolean(ETAPA_POR_CODIGO[codigo] || MACRO_POR_CODIGO[codigo]);
}

export function ehMacro(codigo) {
  return Boolean(MACRO_POR_CODIGO[codigo]);
}

/** Macro-grupo a que a etapa pertence (null quando o código não existe). */
export function macroDaEtapa(codigo) {
  return ETAPA_POR_CODIGO[codigo]?.macro || null;
}

/** Rótulo do vínculo, dizendo quando é o grupo inteiro. */
export function rotuloDoVinculo(codigo) {
  const macro = MACRO_POR_CODIGO[codigo];
  if (macro) return `todo o grupo ${macro.rotulo}`;
  return ETAPA_POR_CODIGO[codigo]?.rotulo || "";
}

/** Etapas que o vínculo cobre: uma (etapa) ou várias (macro-grupo). */
export function etapasDoVinculo(codigo) {
  if (MACRO_POR_CODIGO[codigo]) {
    return ETAPAS_PLANILHA.filter((etapa) => etapa.macro === codigo);
  }
  const etapa = ETAPA_POR_CODIGO[codigo];
  return etapa ? [etapa] : [];
}

/** Ordem do vínculo no fluxo (macro = a da primeira etapa dele). */
export function ordemDoVinculo(codigo) {
  const [primeira] = etapasDoVinculo(codigo);
  return primeira ? primeira.ordem : null;
}

/**
 * Opções do seletor "Etapa da planilha" dos dois formulários de setor, em
 * grupos: um por macro-grupo (com "Todo o grupo" primeiro) só com as etapas
 * que FAZEM O AMBIENTE ANDAR, e por último um grupo das que acontecem em
 * paralelo. Misturadas, a sala ligada a "Terceiros — vidraçaria" parecia uma
 * sala como as outras — e nunca recebia ambiente nenhum pela planilha.
 * → [{rotulo, opcoes: [{valor, texto}]}]
 */
export function gruposDeVinculo() {
  const grupos = MACRO_GRUPOS.map((macro) => {
    const etapas = ETAPAS_PLANILHA.filter((e) => e.macro === macro.codigo && e.tipo === TIPO_ETAPA.SEQUENCIAL);
    if (!etapas.length) return null;
    return {
      rotulo: macro.rotulo,
      opcoes: [
        { valor: macro.codigo, texto: `Todo o grupo — ${macro.rotulo}` },
        ...etapas.map((e) => ({ valor: e.codigo, texto: e.rotulo })),
      ],
    };
  }).filter(Boolean);
  const paralelas = MACRO_GRUPOS.flatMap((macro) => {
    const etapas = ETAPAS_PLANILHA.filter((e) => e.macro === macro.codigo && e.tipo === TIPO_ETAPA.PARALELA);
    const soParalelas = etapas.length && ETAPAS_PLANILHA.every((e) => e.macro !== macro.codigo || e.tipo === TIPO_ETAPA.PARALELA);
    return [
      ...(soParalelas ? [{ valor: macro.codigo, texto: `Todo o grupo — ${macro.rotulo}` }] : []),
      ...etapas.map((e) => ({ valor: e.codigo, texto: e.rotulo })),
    ];
  });
  return [...grupos, { rotulo: "Em paralelo — a sala só recebe o que você mover", opcoes: paralelas }];
}

export const ETAPA_DO_SETOR_FIXO_V2 = Object.freeze({
  corte: "corte",
  bordo: "bordo",
  furacao: "furacao",
  marcenaria: "marcenaria",
  expedicao: "limpeza_embalagem",
});

function texto(valor) {
  return hasValue(valor) ? String(valor).replace(/\s+/g, " ").trim() : "";
}

/** "." (e "X" nas colunas de item) = a planilha diz que a etapa não se aplica. */
export function etapaNaoSeAplica(linha, etapa) {
  const valor = texto(linha?.[etapa?.campo]);
  if (!valor) return false;
  return NAO_SE_APLICA.includes(valor) || valor.toUpperCase() === MARCADOR_X;
}

/**
 * Data da etapa nesta linha, ou null. Só aceita o que é data de verdade:
 * número solto não vale (o espelho da planilha devolve 0, e `new Date("0")`
 * daria 01/01/2000 — marco inventado).
 */
export function dataDaEtapa(linha, etapa) {
  const valor = linha?.[etapa?.campo];
  if (!hasValue(valor)) return null;
  const bruto = texto(valor);
  if (NAO_SE_APLICA.includes(bruto)) return null;
  if (Number.isFinite(Number(bruto.replace(",", ".")))) return null;
  return toDate(valor);
}

/**
 * Onde o ambiente está agora: a etapa sequencial seguinte à mais avançada com
 * data, pulando as que a planilha marcou como não aplicáveis.
 *
 * Devolve `null` quando o fluxo terminou (carregamento com data — o ambiente
 * saiu do mapa) ou quando não há linha. Bandeja de obra futura e de
 * escritório continuam sendo assunto de `derivarSituacao` (setores.js): aqui
 * o ambiente sem liberação de obra aparece na primeira etapa.
 */
export function etapaAtualDoAmbiente(linha) {
  if (!linha) return null;
  const etapas = etapasSequenciaisDa(linha);
  let ultimaComData = -1;
  etapas.forEach((etapa, indice) => {
    if (dataDaEtapa(linha, etapa)) ultimaComData = indice;
  });
  let proxima = ultimaComData + 1;
  while (proxima < etapas.length && etapaNaoSeAplica(linha, etapas[proxima])) {
    proxima += 1;
  }
  return etapas[proxima] || null;
}

/**
 * Item de terceiros/acabamento esperado neste ambiente. A coluna é fórmula
 * espelho da quantidade: vazio, "X" e ZERO significam "não se aplica".
 */
function itemEsperado(valor) {
  if (!hasValue(valor)) return false;
  const bruto = texto(valor);
  if (bruto.toUpperCase() === MARCADOR_X) return false;
  const numero = Number(bruto.replace(",", "."));
  return !(Number.isFinite(numero) && numero === 0);
}

function grupoFechado(linha, codigoGrupo) {
  const fechamento = ETAPAS_PARALELAS.find((etapa) => etapa.fechaGrupo === codigoGrupo);
  return Boolean(fechamento && toDate(linha[fechamento.campo]));
}

function algumItemEsperado(linha, codigoGrupo) {
  return ETAPAS_PARALELAS.filter((etapa) => etapa.grupo === codigoGrupo).some((etapa) =>
    itemEsperado(linha[etapa.campo])
  );
}

function paralelaPendente(linha, etapa) {
  if (etapa.fechaGrupo) {
    return algumItemEsperado(linha, etapa.fechaGrupo) && !toDate(linha[etapa.campo]);
  }
  if (etapa.grupo) {
    if (!itemEsperado(linha[etapa.campo])) return false;
    // `ambientePrecisaItem` é quem sabe reconhecer data travestida (ISO, BR,
    // serial do Excel) na coluna do item: com data, o item já foi resolvido.
    if (!ambientePrecisaItem(linha, { field: etapa.campo })) return false;
    return !grupoFechado(linha, etapa.grupo);
  }
  if (etapa.aplicavelApos) {
    return Boolean(toDate(linha[etapa.aplicavelApos])) && !dataDaEtapa(linha, etapa);
  }
  return !dataDaEtapa(linha, etapa);
}

/** Etapas paralelas que se aplicam a este ambiente e ainda estão abertas. */
export function etapasParalelasPendentes(linha) {
  if (!linha) return [];
  return ETAPAS_PARALELAS.filter((etapa) => paralelaPendente(linha, etapa));
}
